import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Button, Badge, PageHeader, Modal, Input, Select, Alert } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_COLOR = { active:'success', grace_period:'warning', pending_payment:'info', suspended:'danger', deleted:'gray' };
const PLAN_COLOR   = { pack_one:'#2ECC71', pack_two:'#3498DB', pack_advanced:'#F39C12' };
const PLAN_NAMES   = { pack_one:'Pack One', pack_two:'Pack Two', pack_advanced:'Pack Advanced' };

export default function Clients() {
  const [clients, setClients]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter]     = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit]     = useState(false);
  const [showReset, setShowReset]   = useState(false);
  const [selected, setSelected]     = useState(null);
  const [saving, setSaving]         = useState(false);

  const [createForm, setCreateForm] = useState({ name:'', username:'', email:'', password:'', company:'', phone:'', planId:'pack_one', activateNow:true });
  const [editForm, setEditForm]     = useState({});
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => { fetchClients(); }, [page, search, statusFilter, planFilter]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (planFilter) params.append('plan', planFilter);
      const r = await api.get(`/superadmin/clients?${params}`);
      setClients(r.data.clients || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch { toast.error('Failed to load clients'); } finally { setLoading(false); }
  };

  const createClient = async () => {
    if (!createForm.name || !createForm.email || !createForm.password || !createForm.company) return toast.error('Name, email, password and company required');
    setSaving(true);
    try { await api.post('/superadmin/clients', createForm); toast.success('Client created!'); setShowCreate(false); setCreateForm({ name:'',username:'',email:'',password:'',company:'',phone:'',planId:'pack_one',activateNow:true }); fetchClients(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  const updateClient = async () => {
    setSaving(true);
    try { await api.put(`/superadmin/clients/${selected.id}`, editForm); toast.success('Client updated!'); setShowEdit(false); fetchClients(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 8) return toast.error('Password must be at least 8 characters');
    setSaving(true);
    try { await api.post(`/superadmin/clients/${selected.id}/reset-password`, { newPassword }); toast.success('Password reset! Email sent to client.'); setShowReset(false); setNewPassword(''); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  const toggleBlock = async (client) => {
    try { await api.post(`/superadmin/clients/${client.id}/block`); toast.success(client.isBlocked ? 'Client unblocked' : 'Client blocked'); fetchClients(); }
    catch { toast.error('Failed'); }
  };

  const deleteClient = async (client) => {
    if (!window.confirm(`Delete ${client.name}? They have a 48-hour recovery window.`)) return;
    try { await api.delete(`/superadmin/clients/${client.id}`); toast.success('Client deleted (recoverable for 48hrs)'); fetchClients(); }
    catch { toast.error('Failed'); }
  };

  const openEdit = (c) => { setSelected(c); setEditForm({ name: c.name, company: c.company, phone: c.phone, planId: c.planId, status: c.status, emailCredits: c.emailCredits, verifyCredits: c.verifyCredits, accountsLimit: c.accountsLimit, warmupLimit: c.warmupLimit }); setShowEdit(true); };
  const openReset = (c) => { setSelected(c); setNewPassword(''); setShowReset(true); };
  const cf = (k,v) => setCreateForm(p => ({ ...p, [k]: v }));
  const ef = (k,v) => setEditForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <PageHeader title="Client Management" subtitle={`${total} total clients`} action={<Button onClick={() => setShowCreate(true)}>+ Create Client</Button>} />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 Search name, email, company..."
          style={{ flex: 1, minWidth: 200, padding: '9px 14px', background: '#111', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none' }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ padding: '9px 14px', background: '#111', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 10, color: statusFilter ? '#fff' : '#555', fontSize: 13, outline: 'none' }}>
          <option value="">All Status</option>
          {['active','pending_payment','grace_period','suspended','deleted'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
        </select>
        <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
          style={{ padding: '9px 14px', background: '#111', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 10, color: planFilter ? '#fff' : '#555', fontSize: 13, outline: 'none' }}>
          <option value="">All Plans</option>
          {Object.entries(PLAN_NAMES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'rgba(46,204,113,0.05)', borderBottom: '1px solid rgba(46,204,113,0.1)' }}>
                  {['Name','Email','Company','Plan','Status','Expires','Credits','Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: '#555', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#555' }}>No clients found</td></tr>
                ) : clients.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 700, color: '#fff' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#555' }}>@{c.username}</div>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#2ECC71' }}>{c.email}</td>
                    <td style={{ padding: '12px 14px', color: '#888' }}>{c.company}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ color: PLAN_COLOR[c.planId] || '#fff', fontWeight: 700, fontSize: 12 }}>{PLAN_NAMES[c.planId] || c.planId}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <Badge color={STATUS_COLOR[c.status] || 'gray'}>{c.status?.replace(/_/g,' ')}</Badge>
                      {c.isBlocked && <Badge color="danger" style={{ marginLeft: 4 }}>Blocked</Badge>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#666', fontSize: 12 }}>
                      {c.planExpiresAt ? new Date(c.planExpiresAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#666' }}>
                      <div>{c.emailCreditsUsed?.toLocaleString()}/{c.emailCredits === -1 ? '∞' : c.emailCredits?.toLocaleString()}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>✏️</Button>
                        <Button size="sm" variant="ghost" onClick={() => openReset(c)}>🔑</Button>
                        <Link to={`/admin/clients/${c.id}/stats`}><Button size="sm" variant="ghost" title="View stats">📊</Button></Link>
                        <Link to={`/admin/impersonate/${c.id}`}><Button size="sm" variant="ghost" title="View as client">👁</Button></Link>
                        <Button size="sm" variant={c.isBlocked ? 'success' : 'secondary'} onClick={() => toggleBlock(c)}>{c.isBlocked ? '🔓' : '🚫'}</Button>
                        <Button size="sm" variant="danger" onClick={() => deleteClient(c)}>🗑</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px', borderTop: '1px solid rgba(46,204,113,0.06)' }}>
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
              <span style={{ alignSelf: 'center', fontSize: 13, color: '#555' }}>Page {page} of {pages}</span>
              <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
            </div>
          )}
        </Card>
      )}

      {/* ── CREATE CLIENT MODAL ── */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create New Client" size="md">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Full Name *" placeholder="John Smith" value={createForm.name} onChange={e => cf('name', e.target.value)} />
          <Input label="Username" placeholder="johnsmith" value={createForm.username} onChange={e => cf('username', e.target.value)} />
        </div>
        <Input label="Business Email *" type="email" placeholder="john@company.com" value={createForm.email} onChange={e => cf('email', e.target.value)} />
        <Input label="Password *" type="password" placeholder="Min. 8 characters" value={createForm.password} onChange={e => cf('password', e.target.value)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Company *" placeholder="Acme Ltd." value={createForm.company} onChange={e => cf('company', e.target.value)} />
          <Input label="Phone" placeholder="+91 9000000000" value={createForm.phone} onChange={e => cf('phone', e.target.value)} />
        </div>
        <Select label="Plan" value={createForm.planId} onChange={e => cf('planId', e.target.value)}>
          {Object.entries(PLAN_NAMES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#1a1a1a', borderRadius: 10, marginBottom: 16 }}>
          <input type="checkbox" id="activateNow" checked={createForm.activateNow} onChange={e => cf('activateNow', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#2ECC71' }} />
          <label htmlFor="activateNow" style={{ fontSize: 13, color: '#ccc', cursor: 'pointer' }}>Activate immediately (skip payment)</label>
        </div>
        {!createForm.activateNow && <Alert type="info" style={{ marginBottom: 16 }}>Client will need to complete payment before accessing the platform.</Alert>}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => setShowCreate(false)} style={{ flex: 1 }}>Cancel</Button>
          <Button loading={saving} onClick={createClient} style={{ flex: 2 }}>Create Client</Button>
        </div>
      </Modal>

      {/* ── EDIT CLIENT MODAL ── */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={`Edit — ${selected?.name}`} size="md">
        {selected && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Full Name" value={editForm.name || ''} onChange={e => ef('name', e.target.value)} />
              <Input label="Company" value={editForm.company || ''} onChange={e => ef('company', e.target.value)} />
            </div>
            <Input label="Phone" value={editForm.phone || ''} onChange={e => ef('phone', e.target.value)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Select label="Plan" value={editForm.planId || ''} onChange={e => ef('planId', e.target.value)}>
                {Object.entries(PLAN_NAMES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
              <Select label="Status" value={editForm.status || ''} onChange={e => ef('status', e.target.value)}>
                {['active','pending_payment','grace_period','suspended'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </Select>
            </div>
            <div style={{ background: '#1a1a1a', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#2ECC71', fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Credit Override</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Input label="Email Credits" type="number" value={editForm.emailCredits || 0} onChange={e => ef('emailCredits', parseInt(e.target.value))} />
                <Input label="Verify Credits" type="number" value={editForm.verifyCredits || 0} onChange={e => ef('verifyCredits', parseInt(e.target.value))} />
                <Input label="Accounts Limit" type="number" value={editForm.accountsLimit || 0} onChange={e => ef('accountsLimit', parseInt(e.target.value))} />
                <Input label="Warmup Limit" type="number" value={editForm.warmupLimit || 0} onChange={e => ef('warmupLimit', parseInt(e.target.value))} />
              </div>
            </div>
            <Input label="Custom Plan Price (€)" type="number" placeholder="Leave blank to use standard price" value={editForm.planCustomPrice || ''} onChange={e => ef('planCustomPrice', e.target.value)} />
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" onClick={() => setShowEdit(false)} style={{ flex: 1 }}>Cancel</Button>
              <Button loading={saving} onClick={updateClient} style={{ flex: 2 }}>Save Changes</Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── RESET PASSWORD MODAL ── */}
      <Modal open={showReset} onClose={() => setShowReset(false)} title={`Reset Password — ${selected?.name}`} size="sm">
        <p style={{ fontSize: 13, color: '#888', marginBottom: 16, lineHeight: 1.7 }}>
          Set a new password for <strong style={{ color: '#ccc' }}>{selected?.email}</strong>. The new password will be sent to their email address.
        </p>
        <Input label="New Password *" type="password" placeholder="Min. 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <Button variant="secondary" onClick={() => setShowReset(false)} style={{ flex: 1 }}>Cancel</Button>
          <Button loading={saving} onClick={resetPassword} style={{ flex: 2 }}>🔑 Reset & Notify</Button>
        </div>
      </Modal>
    </div>
  );
}
